/**
 * Contact Page Component
 * Lead capture form with validation and company contact info
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import useForm from '../hooks/useForm';
import './Contact.css';

// Check icon for success message
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

// Error icon for form validation
const ErrorIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

// Initial form values
const initialFormValues = {
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    companySize: '',
    developerType: '',
    numberOfDevelopers: '',
    preferredTenure: '',
    message: '',
};

// Validation rules
const validationRules = {
    fullName: ['required'],
    companyName: ['required'],
    email: ['required', 'email'],
    developerType: ['required'],
    preferredTenure: ['required'],
};

// Dropdown options
const companySizeOptions = [
    { value: '', label: 'Select company size' },
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-1000', label: '201-1000 employees' },
    { value: '1000+', label: '1000+ employees' },
];

const developerTypeOptions = [
    { value: '', label: 'Select developer type' },
    { value: 'junior', label: 'Junior Developer' },
    { value: 'senior', label: 'Senior Developer' },
    { value: 'mixed', label: 'Mixed (Junior + Senior)' },
];

const tenureOptions = [
    { value: '', label: 'Select preferred tenure' },
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '9', label: '9 months' },
    { value: '12', label: '12 months' },
    { value: '>12', label: 'More than 12 months' },
];

// Submit form to backend
const submitForm = async (values) => {
    const payload = {
        companyName: values.companyName,
        name: values.fullName,
        email: values.email,
        phone: values.phone,
        message: `
            Project Description: ${values.message}
            
            --- additional details ---
            Company Size: ${values.companySize}
            Developer Type: ${values.developerType}
            Number of Develpers: ${values.numberOfDevelopers}
            Preferred Tenure: ${values.preferredTenure}
        `
    };

    try {
        const response = await fetch('https://api.codemaker.com.my/api/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error('Submission failed');
        }

        return { success: true };
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

function Contact() {
    const {
        isSubmitting,
        isSuccess,
        getFieldProps,
        getFieldError,
        handleSubmit,
        resetForm,
    } = useForm(initialFormValues, validationRules, submitForm);

    return (
        <>
            <Helmet>
                <title>Contact Us | CODEMAKER Technology - Book a Consultation</title>
                <meta
                    name="description"
                    content="Get in touch with CODEMAKER Technology. Book a free consultation to discuss your development needs and learn how our TaaS model can help your business."
                />
            </Helmet>

            <main>
                {/* Hero Section */}
                <section className="contact-hero">
                    <div className="contact-hero__container">
                        <span className="contact-hero__badge">Get Started</span>
                        <h1 className="contact-hero__title">Let's Talk</h1>
                        <p className="contact-hero__description">
                            Ready to transform your development capacity? Fill out the form below
                            and we'll get back to you within 24 hours.
                        </p>
                    </div>
                </section>

                {/* Contact Form Section */}
                <Section>
                    <div className="contact-grid">
                        {/* Form */}
                        <div className="contact-form">
                            {isSuccess ? (
                                // Success Message
                                <div className="contact-form__success">
                                    <div className="contact-form__success-icon">
                                        <CheckIcon />
                                    </div>
                                    <h3 className="contact-form__success-title">Thank You!</h3>
                                    <p className="contact-form__success-text">
                                        Your inquiry has been submitted successfully. Our team will
                                        review your requirements and contact you within 24 hours.
                                    </p>
                                    <Button variant="ghost" onClick={resetForm}>
                                        Submit Another Inquiry
                                    </Button>
                                </div>
                            ) : (
                                // Form
                                <>
                                    <h2 className="contact-form__title">Book a Free Consultation</h2>
                                    <p className="contact-form__subtitle">
                                        Tell us about your project and we'll help you find the right developers.
                                    </p>

                                    <form onSubmit={handleSubmit}>
                                        {/* Name and Company */}
                                        <div className="contact-form__grid contact-form__grid--two-cols">
                                            <div className="form-field">
                                                <label className="form-field__label form-field__label--required" htmlFor="fullName">
                                                    Full Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="fullName"
                                                    className={`form-field__input ${getFieldError('fullName') ? 'form-field__input--error' : ''}`}
                                                    placeholder="John Doe"
                                                    {...getFieldProps('fullName')}
                                                />
                                                {getFieldError('fullName') && (
                                                    <span className="form-field__error">
                                                        <ErrorIcon />
                                                        {getFieldError('fullName')}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="form-field">
                                                <label className="form-field__label form-field__label--required" htmlFor="companyName">
                                                    Company Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="companyName"
                                                    className={`form-field__input ${getFieldError('companyName') ? 'form-field__input--error' : ''}`}
                                                    placeholder="Acme Corporation"
                                                    {...getFieldProps('companyName')}
                                                />
                                                {getFieldError('companyName') && (
                                                    <span className="form-field__error">
                                                        <ErrorIcon />
                                                        {getFieldError('companyName')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Email and Phone */}
                                        <div className="contact-form__grid contact-form__grid--two-cols" style={{ marginTop: 'var(--space-6)' }}>
                                            <div className="form-field">
                                                <label className="form-field__label form-field__label--required" htmlFor="email">
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    className={`form-field__input ${getFieldError('email') ? 'form-field__input--error' : ''}`}
                                                    placeholder="john@company.com"
                                                    {...getFieldProps('email')}
                                                />
                                                {getFieldError('email') && (
                                                    <span className="form-field__error">
                                                        <ErrorIcon />
                                                        {getFieldError('email')}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="form-field">
                                                <label className="form-field__label" htmlFor="phone">
                                                    Phone Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    className="form-field__input"
                                                    placeholder="+60 12-345 6789"
                                                    {...getFieldProps('phone')}
                                                />
                                            </div>
                                        </div>

                                        {/* Company Size and Developer Type */}
                                        <div className="contact-form__grid contact-form__grid--two-cols" style={{ marginTop: 'var(--space-6)' }}>
                                            <div className="form-field">
                                                <label className="form-field__label" htmlFor="companySize">
                                                    Company Size
                                                </label>
                                                <select
                                                    id="companySize"
                                                    className="form-field__select"
                                                    {...getFieldProps('companySize')}
                                                >
                                                    {companySizeOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="form-field">
                                                <label className="form-field__label form-field__label--required" htmlFor="developerType">
                                                    Type of Developer(s) Needed
                                                </label>
                                                <select
                                                    id="developerType"
                                                    className={`form-field__select ${getFieldError('developerType') ? 'form-field__select--error' : ''}`}
                                                    {...getFieldProps('developerType')}
                                                >
                                                    {developerTypeOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {getFieldError('developerType') && (
                                                    <span className="form-field__error">
                                                        <ErrorIcon />
                                                        {getFieldError('developerType')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Number of Developers and Tenure */}
                                        <div className="contact-form__grid contact-form__grid--two-cols" style={{ marginTop: 'var(--space-6)' }}>
                                            <div className="form-field">
                                                <label className="form-field__label" htmlFor="numberOfDevelopers">
                                                    Number of Developers
                                                </label>
                                                <input
                                                    type="number"
                                                    id="numberOfDevelopers"
                                                    className="form-field__input"
                                                    placeholder="1"
                                                    min="1"
                                                    {...getFieldProps('numberOfDevelopers')}
                                                />
                                            </div>

                                            <div className="form-field">
                                                <label className="form-field__label form-field__label--required" htmlFor="preferredTenure">
                                                    Preferred Tenure
                                                </label>
                                                <select
                                                    id="preferredTenure"
                                                    className={`form-field__select ${getFieldError('preferredTenure') ? 'form-field__select--error' : ''}`}
                                                    {...getFieldProps('preferredTenure')}
                                                >
                                                    {tenureOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {getFieldError('preferredTenure') && (
                                                    <span className="form-field__error">
                                                        <ErrorIcon />
                                                        {getFieldError('preferredTenure')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Message */}
                                        <div className="contact-form__grid" style={{ marginTop: 'var(--space-6)' }}>
                                            <div className="form-field">
                                                <label className="form-field__label" htmlFor="message">
                                                    Project Description / Message
                                                </label>
                                                <textarea
                                                    id="message"
                                                    className="form-field__textarea"
                                                    placeholder="Tell us about your project, requirements, timeline, etc."
                                                    {...getFieldProps('message')}
                                                ></textarea>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="contact-form__actions">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                size="large"
                                                fullWidth
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                                            </Button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>

                        {/* Contact Info Sidebar */}
                        <aside className="contact-info">
                            {/* Contact Details */}
                            <div className="contact-info__card">
                                <h3 className="contact-info__title">Contact Information</h3>
                                <div className="contact-info__list">
                                    <div className="contact-info__item">
                                        <div className="contact-info__icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                <polyline points="22,6 12,13 2,6" />
                                            </svg>
                                        </div>
                                        <div className="contact-info__content">
                                            <div className="contact-info__label">Email</div>
                                            <div className="contact-info__value">
                                                <a href="mailto:hello@codemaker.com.my">inquiry@codemaker.com.my</a>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="contact-info__item">
                                        <div className="contact-info__icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                                            </svg>
                                        </div>
                                        <div className="contact-info__content">
                                            <div className="contact-info__label">Phone</div>
                                            <div className="contact-info__value">
                                                <a href="tel:+60312345678">+60 11-1883 6578</a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* <div className="contact-info__item">
                                        <div className="contact-info__icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                        </div>
                                        <div className="contact-info__content">
                                            <div className="contact-info__label">Office</div>
                                            <div className="contact-info__value">
                                                Kuala Lumpur, Malaysia
                                            </div>
                                        </div>
                                    </div> */}
                                </div>

                                {/* Social Links */}
                                {/* <div className="contact-social">
                                    <a
                                        href="https://linkedin.com/company/codemaker"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="contact-social__link"
                                        aria-label="LinkedIn"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                        </svg>
                                    </a>
                                    <a
                                        href="https://facebook.com/codemaker"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="contact-social__link"
                                        aria-label="Facebook"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                        </svg>
                                    </a>
                                    <a
                                        href="https://twitter.com/codemaker"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="contact-social__link"
                                        aria-label="Twitter"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                        </svg>
                                    </a>
                                </div> */}
                            </div>

                            {/* Quick Info */}
                            <div className="quick-info">
                                <h4 className="quick-info__title">Why Contact Us?</h4>
                                <div className="quick-info__list">
                                    <div className="quick-info__item">
                                        <CheckIcon />
                                        <span>Free consultation - no obligation</span>
                                    </div>
                                    <div className="quick-info__item">
                                        <CheckIcon />
                                        <span>Response within 24 hours</span>
                                    </div>
                                    <div className="quick-info__item">
                                        <CheckIcon />
                                        <span>Customized proposal for your needs</span>
                                    </div>
                                    <div className="quick-info__item">
                                        <CheckIcon />
                                        <span>Flexible engagement terms</span>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </Section>
            </main>
        </>
    );
}

export default Contact;
